// Flight instrument v0: one mode-gated radial FUI in the bottom-left —
// amber rings, a pressure needle, arc gauges — bound to real telemetry,
// per the fidelity audit's §e sanction. It appears only at a ship's
// controls and reads entirely in analog gauge language:
//
//   - the PRESSURE DIAL: three sectors (burners / coils / veil); the
//     needle swings to the favored manifold, rests centered at even, and
//     each sector's glow is its meter level;
//   - the SURGE ARC (outer right): boost reserve, flaring while boosting;
//   - the SPEED ARC (outer bottom): sweep grows with speed through the
//     hull's band-implied ceiling;
//   - the LIFT ARC (outer left): height above the local ground toward
//     this sky's commit line (ascent ceiling, or descent floor inverted).
//
// NO DIGITS ANYWHERE — not speed, not altitude, not percentages. Words
// are permitted (bands are words); a needle is a position, not a number.
// The canvas is redrawn each frame; at 260 px it costs nothing.

import type { PowerFocus } from "@bebop/shared";

export interface FlightTelemetry {
  focus: PowerFocus;
  boost: number;
  coil: number;
  veil: number;
  boosting: boolean;
  /** 0..1 of the hull's current ceiling. */
  speedFrac: number;
  /** 0..1 of the sky: height above ground toward the commit altitude. */
  liftFrac: number;
  wearBand: string;
  chargeBand: string;
  /** Which sky this is — flips the lift arc's meaning label. */
  inSpace: boolean;
  /** How close the manifold is to tripping (combat), 0..1. */
  strain: number;
  /** The manifold has tripped: the dial goes dark until the re-light. */
  adrift: boolean;
}

const SIZE = 260;
const AMBER = "#d8b04a";
const AMBER_DIM = "rgba(216, 176, 74, 0.28)";
const HOT = "#ffc46b";
const COOL = "#62d6c4";
const RED = "#e0524a";

/** Sector home angles on the dial (radians, canvas convention). */
const SECTOR_ANGLE: Record<Exclude<PowerFocus, "even">, number> = {
  burners: -Math.PI / 2,
  coils: Math.PI * 0.75,
  veil: Math.PI * 0.25,
};

export class FlightInstrument {
  private root: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private words: HTMLDivElement;
  private reticle: HTMLDivElement;
  private needleAngle = -Math.PI / 2;
  private visible = false;
  private reticleVisible = false;
  /** Previous veil reading, for the absorb flash. */
  private lastVeil = 1;
  private veilFlash = 0;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "flight-instrument";
    this.canvas = document.createElement("canvas");
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.words = document.createElement("div");
    this.words.className = "flight-words";
    this.root.append(this.canvas, this.words);
    this.root.style.display = "none";
    parent.append(this.root);
    this.ctx = this.canvas.getContext("2d");

    // The reticle: the sanctioned hex-reticle half of the instrument
    // family, center screen, only where the gun is live (the Black).
    this.reticle = document.createElement("div");
    this.reticle.className = "coil-reticle";
    this.reticle.style.display = "none";
    parent.append(this.reticle);
  }

  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    this.root.style.display = visible ? "" : "none";
  }

  /** Show the reticle while the coil gun is live (space piloting). */
  setReticle(visible: boolean): void {
    if (visible === this.reticleVisible) return;
    this.reticleVisible = visible;
    this.reticle.style.display = visible ? "" : "none";
  }

  update(view: FlightTelemetry, dt: number): void {
    if (!this.visible) return;
    const ctx = this.ctx;
    if (!ctx) return;
    const c = SIZE / 2;
    ctx.clearRect(0, 0, SIZE, SIZE);

    // The veil absorb flash: any drop in the veil reading blinks the
    // dial's rim — hit feedback without a number anywhere.
    if (view.veil < this.lastVeil - 0.001) this.veilFlash = 1;
    this.lastVeil = view.veil;
    this.veilFlash = Math.max(0, this.veilFlash - dt * 4);

    // Adrift: the whole instrument goes dark and quiet — a dead panel
    // IS the damage readout — with only a faint ember breathing until
    // the re-light.
    ctx.globalAlpha = view.adrift ? 0.35 : 1;

    // Backplate + concentric rings.
    ctx.beginPath();
    ctx.arc(c, c, c - 4, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(10, 9, 6, 0.55)";
    ctx.fill();
    for (const r of [c - 6, c - 30, 46]) {
      ctx.beginPath();
      ctx.arc(c, c, r, 0, Math.PI * 2);
      ctx.strokeStyle = AMBER_DIM;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    if (this.veilFlash > 0 && !view.adrift) {
      ctx.beginPath();
      ctx.arc(c, c, c - 6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(98, 214, 196, ${0.9 * this.veilFlash})`;
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    // --- Pressure dial ------------------------------------------------
    // Three sectors; glow tracks each manifold's meter.
    const sectors: Array<{ key: Exclude<PowerFocus, "even">; level: number; label: string }> = [
      { key: "burners", level: view.boost, label: "burners" },
      { key: "coils", level: view.coil, label: "coils" },
      { key: "veil", level: view.veil, label: "veil" },
    ];
    for (const sector of sectors) {
      const home = SECTOR_ANGLE[sector.key];
      const favored = view.focus === sector.key;
      ctx.beginPath();
      ctx.arc(c, c, 62, home - 0.42, home + 0.42);
      ctx.strokeStyle = favored ? HOT : AMBER_DIM;
      ctx.lineWidth = favored ? 7 : 4;
      ctx.stroke();
      // Meter level as an inner arc filling the same sector.
      ctx.beginPath();
      ctx.arc(c, c, 52, home - 0.42, home - 0.42 + 0.84 * Math.max(0.02, sector.level));
      ctx.strokeStyle = sector.key === "burners" && view.boosting ? HOT : AMBER;
      ctx.lineWidth = 3;
      ctx.stroke();
      // Sector word, small, uppercase — a word, never a number.
      const lx = c + Math.cos(home) * 84;
      const ly = c + Math.sin(home) * 84;
      ctx.font = "600 10px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = favored ? HOT : "rgba(216,176,74,0.75)";
      ctx.fillText(sector.label.toUpperCase(), lx, ly);
    }

    // The needle: swings to the favored sector; rests up at even.
    const target = view.focus === "even" ? -Math.PI / 2 : SECTOR_ANGLE[view.focus];
    let delta = target - this.needleAngle;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    this.needleAngle += delta * Math.min(1, dt * 10);
    ctx.beginPath();
    ctx.moveTo(c - Math.cos(this.needleAngle) * 10, c - Math.sin(this.needleAngle) * 10);
    ctx.lineTo(c + Math.cos(this.needleAngle) * 44, c + Math.sin(this.needleAngle) * 44);
    ctx.strokeStyle = view.focus === "even" ? AMBER : HOT;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(c, c, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = AMBER;
    ctx.fill();

    // --- Outer arcs ---------------------------------------------------
    const arc = (
      from: number,
      span: number,
      frac: number,
      color: string,
      trackColor = AMBER_DIM,
    ) => {
      ctx.beginPath();
      ctx.arc(c, c, c - 16, from, from + span);
      ctx.strokeStyle = trackColor;
      ctx.lineWidth = 5;
      ctx.stroke();
      if (frac > 0.01) {
        ctx.beginPath();
        ctx.arc(c, c, c - 16, from, from + span * Math.min(1, frac));
        ctx.strokeStyle = color;
        ctx.lineWidth = 5;
        ctx.stroke();
      }
    };
    // Surge (boost reserve): right side, sweeping up; flares hot in use.
    arc(Math.PI * 0.35, -Math.PI * 0.6, view.boost, view.boosting ? HOT : AMBER);
    // Speed: bottom, sweeping right.
    arc(Math.PI * 0.6, Math.PI * 0.35, view.speedFrac, HOT);
    // Lift: left side, sweeping up; teal — the sky's own color. Near the
    // commit line it warns red: the next thing that happens is a handoff.
    const nearCommit = view.inSpace ? view.liftFrac < 0.08 : view.liftFrac > 0.92;
    arc(Math.PI * 0.65, Math.PI * 0.6, view.liftFrac, nearCommit ? RED : COOL);

    // Strain: an inner red arc under the dial — red is warnings only,
    // and this is the one true warning: at full sweep the manifold trips.
    if (view.strain > 0.005) {
      ctx.beginPath();
      ctx.arc(c, c, 38, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, view.strain));
      ctx.strokeStyle = RED;
      ctx.lineWidth = 4;
      ctx.stroke();
    }

    ctx.globalAlpha = 1;

    // The words: the two band words and where the pressure sits — or the
    // trip. Words only; this line is the instrument's entire verbal
    // surface.
    const routing = view.focus === "even" ? "pressure even" : `pressure to the ${view.focus}`;
    const text = view.adrift
      ? "she's adrift — the manifold re-lights itself"
      : `${routing} · running ${view.wearBand} · charge ${view.chargeBand}`;
    if (this.words.textContent !== text) this.words.textContent = text;
    this.words.style.color = view.adrift || view.chargeBand === "dry" ? RED : "";
  }

  dispose(): void {
    this.root.remove();
    this.reticle.remove();
  }
}
